import {
  type PlaceSiteTypeId,
  placeSiteTypeId,
} from '@/features/locations/constants/placeSiteTypes';
import { paintedPlaceMaki } from '@/features/locations/utils/makiPaths';
import type { INearbyPlace } from '@/types/api';

// Mixpanel event names for the deals feature. Centralised here so every emit
// site references the same string constant — drift between caller and
// dashboard is the most common analytics bug.

export const DEALS_MIXPANEL_EVENTS = {
  /** A deal card opened on a deal the query has resolved. */
  DEAL_INSPECTED: 'deal_inspected',
  /** The card's HubSpot deep link was followed. */
  HUBSPOT_OPENED: 'deal_hubspot_opened',
  /** The card was flipped to the attached location's metrics. */
  LOCATION_METRICS_OPENED: 'deal_location_metrics_opened',
  /** The deal-stage chip selection settled (debounced). */
  STAGE_FILTERS_APPLIED: 'deal_stage_filters_applied',

  /** The creation dialog was opened on a location. */
  CREATION_OPENED: 'deal_creation_opened',
  /** A form step was left forward, which is the step the BD finished. */
  CREATION_STEP_COMPLETED: 'deal_creation_step_completed',
  /** A form step was left backward. */
  CREATION_STEP_BACK: 'deal_creation_step_back',
  /** A CRM lookup answered. */
  CREATION_CRM_SEARCHED: 'deal_creation_crm_searched',
  /** A place from the nearby list was picked as the deal's POI. */
  CREATION_POI_PICKED: 'deal_creation_poi_picked',
  /** The dialog was closed on a deal that never landed. */
  CREATION_ABANDONED: 'deal_creation_abandoned',
  /** The form was submitted, which starts a run. */
  CREATION_SUBMITTED: 'deal_creation_submitted',
  /** A run landed its deal. */
  CREATION_SUCCEEDED: 'deal_creation_succeeded',
  /** A run stopped, or the request that starts one was refused. */
  CREATION_FAILED: 'deal_creation_failed',
} as const;

export type DealsMixpanelEvent = (typeof DEALS_MIXPANEL_EVENTS)[keyof typeof DEALS_MIXPANEL_EVENTS];

// Per-event payload shape contracts. The runtime emit site validates nothing —
// these types are the contract.

/**
 * How the card being read came to be open. A restored `?deal=` and a shared link are both
 * `deep_link`: neither is an act inside the session, and nothing distinguishes them client-side.
 * `search` is a pick in the map search bar.
 */
export type DealOpenSource = 'map_click' | 'deep_link' | 'just_created' | 'search';

export interface DealInspectedPayload {
  deal_id: string;
  stage: string;
  phase: string;
  /** 0 for a stage out of the live pipeline, which is what `dealStagePosition` reports. */
  stage_position: number;
  /** Null while the API has not sent `entered_stage_at`, which is what an older backend gives. */
  days_in_stage: number | null;
  has_location_results: boolean;
  has_zone: boolean;
  has_hubspot_url: boolean;
  is_own_deal: boolean;
  source: DealOpenSource;
}

export interface DealHubSpotOpenedPayload {
  deal_id: string;
  /** Absent for a deal whose creation run reported no stage back. */
  stage?: string;
  /** Reading an existing deal, or following the link a creation just offered. */
  origin: 'card' | 'creation_success';
}

export interface DealLocationMetricsOpenedPayload {
  deal_id: string;
  stage: string;
  has_forecast: boolean;
}

export interface DealStageFiltersAppliedPayload {
  /** The stages left visible, in pipeline order. */
  visible_stages: string[];
  visible_count: number;
  hidden_count: number;
  /** Whether the settled selection came from one chip or the section's show/hide-all. */
  scope: 'single' | 'all';
}

/** Ticking four stages is four calls but one intent, so the emit carries the settled selection. */
export const DEAL_FILTER_TRACKING_DEBOUNCE_MS = 500;

// ── Creation funnel ─────────────────────────────────────────────────────────────
// The form's own steps and the run's are two different things: these name the
// form's, which are the ones a drop-off happens between. The run's steps are
// `DealCreationStep`, and only the failure event reports one.

export type DealFormStep = 'company' | 'contact' | 'deal';

/** Whether the step's record is attached from the CRM or created alongside the deal. */
export type DealRecordMode = 'attach' | 'create';

export interface DealCreationOpenedPayload {
  location_id: string;
  /** Whether the deal name came pre-filled, which is the common path. */
  has_address: boolean;
}

export interface DealCreationStepCompletedPayload {
  step: DealFormStep;
  /** Absent on the deal step, which has no record to attach. */
  mode?: DealRecordMode;
  /** How many of the step's optional fields carry a value — how much the BD volunteers. */
  filled_optional_count: number;
}

export interface DealCreationStepBackPayload {
  from_step: DealFormStep;
  to_step: DealFormStep;
}

export interface DealCreationCrmSearchedPayload {
  record_type: 'company' | 'contact';
  result_count: number;
  matched: boolean;
}

export interface DealCreationPoiPickedPayload {
  /** The filter chip the place's icon belongs to. Other when the icon decides none. */
  site_type: PlaceSiteTypeId;
  /** The icon the map paints. Absent when the place has none, as on the map events. */
  maki?: string;
  /** The place's row in the list as the API offered it, 1 for the nearest. */
  rank: number;
  /** Whether the BD had typed a fragment that narrowed the list before picking. */
  filtered: boolean;
  distance_m: number;
}

export function dealCreationPoiPickedPayload(
  place: INearbyPlace,
  offered: readonly INearbyPlace[],
  filtered: boolean,
): DealCreationPoiPickedPayload {
  const maki = paintedPlaceMaki(place.maki ?? '', place.maki_beta ?? '');
  return {
    site_type: placeSiteTypeId(maki),
    ...(maki && maki !== 'marker' ? { maki } : {}),
    rank: offered.indexOf(place) + 1,
    filtered,
    distance_m: Math.round(place.distance),
  };
}

export interface DealCreationAbandonedPayload {
  /** The step the dialog was closed on. */
  step: DealFormStep;
  /** The furthest step reached, which a BD who went back is no longer standing on. */
  furthest_step: DealFormStep;
  elapsed_ms: number;
  /** Whether a run had already failed, which is a correction given up on rather than a drop-off. */
  after_failure: boolean;
}

export interface DealCreationSubmittedPayload {
  company_mode: DealRecordMode;
  contact_mode: DealRecordMode;
  has_owner: boolean;
  has_zone_ref: boolean;
  /** Whether the owner is still the BD the field defaulted to. */
  owner_is_self: boolean;
  /** 1 for the first submit of this dialog, higher for a correction after a failure. */
  attempt: number;
}

export interface DealCreationSucceededPayload {
  deal_id: string;
  /** How long the run itself took, from the submit that started it. */
  run_ms: number;
  attempt_count: number;
}

export interface DealCreationFailedPayload {
  /** The run's step, absent when the request that starts the run was itself refused. */
  step?: string;
  failure_reason: string;
  /** The refusal's status where there was one — 409 is a location that already carries a deal. */
  http_code?: number;
  run_ms: number;
  attempt_count: number;
}
